import React from 'react';
import { withRouter } from 'react-router';
import compose from 'lodash/fp/compose';

import './AssessmentInformedResultsSummary.scss';

import {
  PREAP_ENG1,
  PREAP_ENG2,
  PREAP_WHG,
} from 'chameleon-src/constants/subjects';
import sortTags, { sortBy } from './utility/tagSorter';

import getQuestionsByTag from './utility/questionsByTag';
import withAssessmentFocusLinks from './withAssessmentFocusLinks';
import AreaForGrowthDisplay from './AreaForGrowthDisplay';
import getDisplayName from './utility/tagDisplayName';

/**
 * Accepts a list of tags, the subjectId, and the list of focus links, then
 * combines them into an AreaForGrowth object which can be displayed in the
 * Area for Growth section of the aggregate summary.
 * @param tags - list of tags for the subject
 * @param subjectId - the subject ID
 * @param focusLinks - The list of focus links for this assignment
 * @returns {object} - An list of objects used to display the area for growth information about a tag.
 */
const getAreasForGrowth = (tags, subjectId, focusLinks) =>
  tags.map((tag) => ({
    name: tag.value,
    itemsReferences: tag.items,
    focusLinks: (focusLinks || []).filter((lesson) => lesson.tag === tag.value),
    displayName: getDisplayName(tag.value, subjectId),
    status: {
      rating: tag.avg_scores,
      questionCount: tag.items.length,
    },
  }));

const bodyHeaderLabel = (course, category = 'WHG Item Type') => {
  if (category !== 'WHG Item Type') return category;
  switch (parseInt(course, 10)) {
    case PREAP_ENG1:
    case PREAP_ENG2:
      return 'Literacy Strands';
    case PREAP_WHG:
      return 'Disciplinary Skills';
    default:
      return 'Areas of Focus';
  }
};

const assessmentsLabel = (course) => {
  switch (parseInt(course, 10)) {
    case PREAP_ENG1:
    case PREAP_ENG2:
      return 'Recommended lessons based upon performance in the literacy strands';
    case PREAP_WHG:
      return 'Recommended lessons based upon performance in the disciplinary skills';
    default:
      return 'Recommended lessons based upon performance in the areas of focus';
  }
};

const AssessmentInformedResultSummary = ({
  assignments,
  focusLinks,
  lastScoreByItem,
  lastScoreByTag,
  loading,
  params,
  questionDataset,
  router,
  showInfo,
  studentView,
  subject,
  useClassroomResourceAddress,
  userData,
  showLearningCheckpointEnhancedTab,
}) => {
  if (loading) {
    return null;
  }
  const { isStudent, isTeacher, isAdmin, isCoordinator } = userData;
  const { studentId } = params;
  const isAdminOrCoordinator = isAdmin || isCoordinator;

  const areasOfFocus = sortTags(
    getAreasForGrowth(lastScoreByTag.tags, subject.id, focusLinks),
    subject.id,
    sortBy.rating,
  );
  let areasForGrowth = getQuestionsByTag(
    areasOfFocus,
    lastScoreByItem,
    questionDataset,
    assignments,
    studentId,
  );

  if (isStudent || studentView) {
    areasForGrowth = sortTags(
      areasForGrowth,
      subject.id,
      sortBy.averagePercentage,
    );
  }

  const header = (
    <div className="info-title-header">
      <div className="info-title">Using Assessments to Inform Instruction</div>
      <div className="info-subheading">{assessmentsLabel(subject.id)}</div>
    </div>
  );

  return areasForGrowth.length ? (
    <div className="info-summary-result">
      {!studentView ? header : null}
      <div className="info-summary-body">
        <div className="info-summary-header">
          <span>{bodyHeaderLabel(subject.id)}</span>
          <span>{!studentView ? 'Class' : 'Your'} Performance</span>
        </div>
        <div className="info-summary-content">
          {areasForGrowth.map((area, index, areas) => (
            <AreaForGrowthDisplay
              key={area.name}
              area={area}
              subjectId={subject.id}
              studentId={studentId}
              isTeacher={isTeacher}
              isStudent={isStudent}
              isAdminOrCoordinator={isAdminOrCoordinator}
              borderBottom={areas.length - 1 !== index}
              router={router}
              studentView={studentView}
              useClassroomResourceAddress={useClassroomResourceAddress}
              showLearningCheckpointEnhancedTab={
                showLearningCheckpointEnhancedTab
              }
            />
          ))}
        </div>
      </div>
    </div>
  ) : showInfo ? (
    `We were unable to load information about these ${bodyHeaderLabel(subject.id)}.`
  ) : null;
};

export default compose(
  withAssessmentFocusLinks,
  withRouter,
)(AssessmentInformedResultSummary);
